// Centralized initialization of all stores in the correct order
// Dependency injection through constructors for explicit dependencies

import { isEqual } from 'lodash-es';
import { reaction, untracked } from 'mobx';
import { isHydrated } from 'mobx-persist-store';

import { DeliveryMethods } from '~/api/ETA';
import { company } from '~/company/Company';
import {
  createMembershipCardChangeStore,
  createMembershipManageStore,
} from '~/modules/membership/createMembershipManageStores';
import {
  createMembershipPurchaseStore,
  createMembershipStore,
} from '~/modules/membership/createMembershipStore';
import { setGlobalContextProvider } from '~/services/telemetry';
import {
  deliveryMethodForUsage,
  isSameAddressSaved,
} from '~/utils/deliveryMethod';
import { getInstallId } from '~/utils/installId';

import { BindCardStore } from './BindCardStore';
import { CartStore } from './CartStore';
import { CatalogStore } from './CatalogStore/CatalogStore';
import { CheckoutStore } from './CheckoutStore/CheckoutStore';
import { DeliveryMethodAvailabilityStore } from './DeliveryMethodAvailabilityStore';
import { MainStore } from './MainStore';
import { OrderStore } from './OrderStore/OrderStore';
import { SettingsStore } from './Settings';
import { UserStore } from './UserStore';

export const settingStore = new SettingsStore();
export const userStore = new UserStore(settingStore);
export const catalogStore = new CatalogStore(userStore);
export const orderStore = new OrderStore(userStore);
export const deliveryMethodAvailabilityStore =
  new DeliveryMethodAvailabilityStore(userStore, orderStore);
export const checkoutStore = new CheckoutStore(userStore);
export const mainStore = new MainStore(userStore);
export const membershipStore = createMembershipStore(orderStore, userStore);
export const cartStore = new CartStore(
  userStore,
  orderStore,
  checkoutStore,
  mainStore,
  membershipStore,
);
export const bindCardStore = new BindCardStore(
  orderStore,
  checkoutStore,
  mainStore,
);
export const membershipPurchaseStore = createMembershipPurchaseStore(
  membershipStore,
  userStore,
);
export const membershipManageStore =
  createMembershipManageStore(membershipStore);
export const membershipCardChangeStore = createMembershipCardChangeStore(
  membershipStore,
  userStore,
);

const telemetryDeviceId = (): string | undefined => {
  if (!mainStore.isRN) {
    return getInstallId() ?? undefined;
  }

  const { deviceId } = userStore;

  return deviceId && deviceId !== '00000000-0000-0000-0000-000000000000'
    ? deviceId
    : undefined;
};

/**
 * Stamps every event with the tenant, the surface it ran on, and the native
 * app version.
 *
 * Registered here because this is the first module where all three are
 * reachable, and read through a callback so the telemetry module keeps
 * importing no store — the stores report, so importing one there would close
 * the cycle. `company` is deliberately read per call rather than captured:
 * on the server it is assigned per request, so a value read once at module
 * scope freezes on the default tenant.
 *
 * `untracked` because analytics now reads this on every event, and a `track()`
 * call made from an observer's render or from a reaction would otherwise
 * subscribe that caller to `appPlatform` and `appVersion` — fields the native
 * handshake fills in mid-session, re-rendering a component for no reason other
 * than that it reported something.
 */
setGlobalContextProvider(() =>
  untracked(() => ({
    company: company.name,
    // `appPlatform` is empty until the native handshake lands, and a handshake
    // that never lands is itself a thing being investigated. Collapsing that
    // window into 'rn-android' would file every early iOS session under Android.
    surface: mainStore.isRN
      ? mainStore.appPlatform === 'ios'
        ? 'rn-ios'
        : mainStore.appPlatform === 'android'
          ? 'rn-android'
          : 'rn-unknown'
      : 'web',
    appVersion: mainStore.appVersion || undefined,
    appBuild: mainStore.appBuild || undefined,
    deviceId: telemetryDeviceId(),
  })),
);

export { BindCardStore } from './BindCardStore';
export { CartStore } from './CartStore';
export { CatalogStore } from './CatalogStore/CatalogStore';
export { CheckoutStore } from './CheckoutStore/CheckoutStore';
export { MainStore } from './MainStore';
export { OrderStore } from './OrderStore/OrderStore';
export { SettingsStore } from './Settings';
export { UserStore } from './UserStore';

// MobX reactions that use multiple stores
// Must be defined after all stores are initialized

reaction(
  () => ({
    providers: { ...orderStore.deliveryStore.deliveryProviderByMethod },
    method: checkoutStore.deliveryMethod,
    pup: cartStore.v3SelectedPupId ?? cartStore.v3SelectedPupExternalId ?? null,
    usage: userStore.deliveryAddress?.usage ?? null,
  }),
  (next, prev) => {
    // biome-ignore lint/suspicious/noConsole: temporary JS-13586 trace
    console.warn(
      '[JS-13586] delivery state',
      { prev, next },
      new Error().stack,
    );
  },
  { equals: isEqual, fireImmediately: true },
);

reaction(
  () => userStore.isAuthorized,
  () => {
    if (userStore.isAuthorized) {
      userStore.fetchAddresses();
    }
  },
);

reaction(
  () => userStore.isAuthorized,
  () => {
    if (userStore.isAuthorized) {
      catalogStore.notifyMeStore.getNotifyMeSKUList();
    }
  },
);

reaction(
  () => userStore.isAuthorized,
  () => {
    if (userStore.isAuthorized) {
      userStore.loyaltyPointsStore.requestTerms();
    }
  },
  { equals: isEqual },
);

reaction(
  () => userStore.loyaltyPointsStore.showLoyaltyPointsOnFrontend,
  () => {
    if (userStore.loyaltyPointsStore.showLoyaltyPointsOnFrontend) {
      userStore.loyaltyPointsStore.getLoyaltyUserBalanceAndTransactions();
    }
  },
  { equals: isEqual },
);

reaction(
  () => cartStore.calculationProcess.isLoading,
  () => {
    if (!cartStore.calculationProcess.isLoading) {
      checkoutStore.slotSelectionModalStore.setIsLoading(false);
    }
  },
);

reaction(
  () =>
    cartStore.cart.map(({ id, sku, count, selected }) => ({
      id,
      sku,
      count,
      selected,
    })),
  () => {
    if (!isHydrated(checkoutStore) || !isHydrated(cartStore)) {
      return;
    }

    checkoutStore.calculateDeliveries(checkoutStore.sortedDeliverySlotsList);
  },
  { equals: isEqual },
);

// The pickup-slot policy comes from ETA until the first /calculate of a
// click-and-collect cart (or a rejection) states it. Whenever it changes, the
// pickup delivery may need a slot it did not have (enabled, required) or must drop
// one (disabled). The verdict counter is tracked as well, so a rejection
// restating the policy already in memory still rebuilds the delivery that
// provoked it.
reaction(
  () => [
    checkoutStore.pickupSlotsPolicy.enabled,
    checkoutStore.pickupSlotsPolicy.required,
    cartStore.pickupSlotsPolicyVerdict,
  ],
  () => {
    if (!isHydrated(checkoutStore) || !isHydrated(cartStore)) {
      return;
    }
    if (checkoutStore.deliveryMethod !== DeliveryMethods.CLICK_AND_COLLECT) {
      return;
    }
    checkoutStore.calculateDeliveries(checkoutStore.sortedDeliverySlotsList);
  },
  { equals: isEqual },
);

// The policy describes one pickup point's click-and-collect cart. Leaving
// self-collection, or switching to a different pickup point without leaving it,
// makes the current verdict meaningless: a stale `enabled`/`required` would let
// the new pickup point show (or hide) a picker before its own /calculate has
// answered. Forgetting it hands the decision back to that point's ETA answer.
// That answer is usually the same `enabled` as before, so the policy reaction
// above stays quiet, and the slot-pool reaction below keys on day timestamps,
// which every point shares: rebuild here, or the delivery keeps the previous
// point's window.
reaction(
  () =>
    [
      checkoutStore.deliveryMethod,
      orderStore.etaCalculation?.warehouse.code,
    ] as const,
  ([method, warehouseCode], [previousMethod, previousWarehouseCode]) => {
    if (method !== DeliveryMethods.CLICK_AND_COLLECT) {
      cartStore.resetPickupSlotsPolicy();
      return;
    }

    if (
      previousMethod === DeliveryMethods.CLICK_AND_COLLECT &&
      previousWarehouseCode !== undefined &&
      previousWarehouseCode !== warehouseCode
    ) {
      cartStore.resetPickupSlotsPolicy();

      if (isHydrated(checkoutStore) && isHydrated(cartStore)) {
        checkoutStore.calculateDeliveries(
          checkoutStore.sortedDeliverySlotsList,
        );
      }
    }
  },
  { equals: isEqual },
);

// Rebuild deliveries whenever the pool of available delivery slots changes
// (e.g. address switch to another zone). Without this, deliveries[].slot
// references stale schedule_slot_id values from the previous zone and the
// slot selector renders empty.
reaction(
  () => Object.keys(checkoutStore.deliverySlots ?? {}).join(','),
  () => {
    if (!isHydrated(checkoutStore) || !isHydrated(cartStore)) {
      return;
    }
    checkoutStore.calculateDeliveries(checkoutStore.sortedDeliverySlotsList);
  },
);

// Reactions from OrderStore
reaction(
  () => orderStore.etaCalculation?.warehouse.code,
  () => {
    if (userStore.isAuthorized) {
      catalogStore.notifyMeStore.getNotifyMeSKUList(
        orderStore.etaCalculation?.warehouse.code,
      );
    }
  },
);

reaction(
  () => [
    orderStore.etaCalculation?.warehouse.zone,
    checkoutStore.deliveryMethod,
  ],
  () => {
    const targetZone =
      orderStore.etaCalculation?.warehouse.zone?.[checkoutStore.deliveryMethod];

    if (targetZone) {
      mainStore.sendToRN('sendTags', {
        USER_ZONE: targetZone?.name,
      });
    }
  },
);

reaction(
  () => {
    const address = userStore.deliveryAddress;
    if (!address) return null;
    return (
      address.addressId ||
      `${address.coordinates.lat}:${address.coordinates.lng}`
    );
  },
  (addressKey, prevAddressKey) => {
    if (!company.hasNewLogistics) return;
    if (!prevAddressKey || !addressKey || prevAddressKey === addressKey) return;

    cartStore.resetV3RouteSelection();
  },
);

// Availability changed — slots might already be stale for existing
// deliveries. Drop expired slot references and report the change, but
// do NOT refetch ETA here: setEtaCalculation has just written fresh
// data, and calling reCalculateDeliveries would trigger a redundant
// requestETA round-trip that would itself write etaCalculation a
// second time, causing a duplicate /calculate refetch.
reaction(
  () =>
    Object.values(
      orderStore.etaCalculation?.warehouse.availability.deliveryMechanics ?? {},
    ).map((i) => i.availableNow),
  () => {
    if (!isHydrated(checkoutStore) || !isHydrated(userStore)) {
      return;
    }

    checkoutStore.checkSlotExpirationOfExistingDeliveries(
      checkoutStore.sortedDeliverySlotsList,
    );
    checkoutStore.slotsChangedEventStore.checkDifferenceWithPreviousDeliveries();
  },
  { equals: isEqual },
);

// Reactions from CheckoutStore
reaction(
  () => checkoutStore.isDeliverTogether,
  () => {
    if (!isHydrated(checkoutStore) || !isHydrated(cartStore)) {
      return;
    }

    checkoutStore.calculateDeliveries(checkoutStore.sortedDeliverySlotsList);
    // Every caller of switchDeliverTogether() is combining or splitting the
    // customer's own deliveries, not reacting to a backend change: resync here,
    // once the rebuild above has actually run, rather than at each call site
    // where deliveries are still the pre-toggle snapshot.
    checkoutStore.slotsChangedEventStore.preserveDeliveryInfo();
  },
);

reaction(
  () => checkoutStore.deliveryMethod,
  async () => {
    try {
      if (
        !isHydrated(checkoutStore) ||
        !isHydrated(userStore) ||
        orderStore.isETALoading
      ) {
        return;
      }

      await orderStore.requestETA();
      checkoutStore.calculateDeliveries(checkoutStore.sortedDeliverySlotsList);
    } finally {
      checkoutStore.deliveryMethodChangeMeta.setLoading(false);
    }
  },
);

// Switcher only: the app-wide ETA is scoped to the selected delivery method,
// so a method with no warehouse at the current point yields an empty catalog.
// The two availability probes are the source of truth for what is actually
// available here — realign the selected method to an available one so the ETA
// re-runs for a method that resolves a warehouse.
reaction(
  () => ({
    methods: deliveryMethodAvailabilityStore.availableMethods,
    // Tracked so the reaction re-runs the moment both probes settle, even when
    // `methods` itself did not change on that final transition.
    settled: !deliveryMethodAvailabilityStore.isProbing,
  }),
  ({ methods, settled }) => {
    if (!company.showDeliveryMethodSwitcher) {
      return;
    }

    // While a probe is still in flight an unresolved method reads as
    // unavailable; realigning now could flip the method to the only
    // already-resolved one (e.g. pickup before courier) and then never switch
    // back. Wait until availability is trustworthy.
    if (!settled) {
      return;
    }

    const [firstMethod] = methods;

    if (firstMethod && !methods.includes(checkoutStore.deliveryMethod)) {
      checkoutStore.setDeliveryMethod(firstMethod);
    }
  },
  { equals: isEqual },
);

// Switcher only: the delivery method follows the active address's saved usage.
// A pickup-tagged address defaults to pickup, everything else (delivery-tagged
// or untagged) defaults to courier delivery. Runs on address change, so an
// in-session header toggle for the current address is preserved; the
// availableMethods reaction above still corrects the method if the usage
// default is not actually available at that point. Saving the current address
// (null id → new id at the same point) is not an address change.
reaction(
  () => ({
    addressId: userStore.deliveryAddress?.addressId,
    coordinates: userStore.deliveryAddress?.coordinates && {
      ...userStore.deliveryAddress.coordinates,
    },
  }),
  (next, prev) => {
    if (!company.showDeliveryMethodSwitcher) {
      return;
    }

    const address = userStore.deliveryAddress;

    if (!address || isSameAddressSaved(prev, next)) {
      return;
    }

    checkoutStore.setDeliveryMethod(deliveryMethodForUsage(address.usage));
  },
  { equals: (a, b) => a.addressId === b.addressId },
);

// "No warehouse serves this address" is a verdict on one point, so moving to
// another starts without it. The next ETA re-establishes the truth either way;
// leaving the flag up until then would name the new address unusable before
// anything asked about it.
//
// Keyed on the coordinates rather than `addressId`, which is null for every
// guest address and unchanged when a saved one is edited in place — both cases
// would otherwise stay blocked on a verdict about the point they left.
reaction(
  () => userStore.deliveryAddress?.coordinates,
  () => {
    orderStore.setIsDeliveryUnavailable(false);
  },
  { equals: isEqual },
);

// Switcher only: there the ETA is requested for one method, so its rejection
// means "not by this method" and cannot condemn the address — which is why
// OrderStore ignores it. The probes answer the whole question, so they are what
// sets the flag for these tenants.
reaction(
  () => deliveryMethodAvailabilityStore.isAddressUnserved,
  (isUnserved) => {
    if (!company.showDeliveryMethodSwitcher) {
      return;
    }

    orderStore.setIsDeliveryUnavailable(isUnserved);
  },
);
